import React, { useMemo, useState } from "react";
import { ReactSVG } from "react-svg";
import arrowIcon from "../../assets/svg/arrow-s.svg";
import { copyTextToClipboardSafe } from "../APanel/CalendarPlus/utils";
import UIIcon from "./UIIcon";
import UserAvatar from "./UserAvatar";

const escapeRegExp = (s = "") => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function highlight(text, q, highlightClassName) {
  const t = String(text ?? "");
  const qq = String(q ?? "").trim();
  if (!qq) return t;

  const safe = escapeRegExp(qq);
  const parts = t.split(new RegExp(`(${safe})`, "gi"));

  return parts.map((part, index) =>
    part.toLowerCase() === qq.toLowerCase() ? (
      <i key={index} className={highlightClassName}>
        {part}
      </i>
    ) : (
      part
    ),
  );
}

export default function StudentItem({
  student,
  color,
  initials,
  onOpen,
  highlightQuery = "",
  secondaryText, // ex: phone
  showChevron = true,
  endAdornment = null,
  className = "",
  highlightClassName = "studentItem__highlight",
  // buton mic de copiere a telefonului, între număr și stare; fundalul lui
  // are culoarea stării și ține loc de separator
  phoneCopy = false,
  // starea programării, ca text simplu după buton („Întârziat")
  statusText = "",
  statusTone = null,
  statusTitle = "",
}) {
  const isInteractive = typeof onOpen === "function";
  const [copied, setCopied] = useState(false);

  const fullName = useMemo(() => {
    const fn = String(student?.firstName || "").trim();
    const ln = String(student?.lastName || "").trim();
    return `${fn} ${ln}`.trim();
  }, [student]);

  const secondary = secondaryText ?? student?.phone ?? "–";
  const phoneToCopy = String(student?.phone || "").trim();

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen?.(student);
    }
  };

  const handleCopyPhone = async (event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!phoneToCopy) return;
    const ok = await copyTextToClipboardSafe(phoneToCopy);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };

  const statusLabel = String(statusText || "").trim();
  const copyTitle = copied ? "Copiat" : "Copiază telefonul";

  return (
    <div
      className={`studentItem ${className}`}
      onClick={isInteractive ? () => onOpen(student) : undefined}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
    >
      <UserAvatar
        person={student}
        color={color}
        initials={initials}
        className="studentItem__avatar"
      />

      <div className="studentItem__info">
        <h3 className="studentItem__name">
          {highlight(fullName || "–", highlightQuery, highlightClassName)}
        </h3>
        <p className="studentItem__meta">
          <span className="studentItem__metaRow">
            <span className="studentItem__metaPhone">
              <span className="studentItem__metaPhoneText">
                {highlight(secondary, highlightQuery, highlightClassName)}
              </span>
              {phoneCopy && phoneToCopy ? (
                <button
                  type="button"
                  className={
                    "studentItem__copyBtn" +
                    (statusTone ? ` studentItem__copyBtn--${statusTone}` : "") +
                    (copied ? " is-copied" : "")
                  }
                  title={copyTitle}
                  aria-label={copied ? "Telefon copiat" : "Copiază telefonul"}
                  onClick={handleCopyPhone}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <UIIcon name="copy" className="studentItem__copyIcon" />
                </button>
              ) : null}
            </span>

            {statusLabel ? (
              <span
                className="studentItem__status"
                title={statusTitle || statusLabel}
              >
                {statusLabel}
              </span>
            ) : null}
          </span>
        </p>
      </div>

      {endAdornment ? (
        <div className="studentItem__end">{endAdornment}</div>
      ) : showChevron ? (
        <div className="studentItem__chev" aria-hidden="true">
          <ReactSVG className="studentItem__chevIcon" src={arrowIcon} />
        </div>
      ) : null}
    </div>
  );
}
